import React, { useEffect, useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { MeetingContext } from "./MeetingProvider";
import {
  fetchCurrentReception,
  fetchMeetingDetails,
  guestArrivesAtReception,
} from "../api/xchange-api";
import {
  MEETING_QUERY_PARAMS,
  MEETING_QUERY_PARAMS_GUESTS,
  APP_EVENT_ID,
} from "../utils/consts";
import { UserContext, USER_ROLES } from "./UserProvider";
import useNotifyOnMeetingLeave from "../hooks/useNotifyOnMeetingLeave";
import ReceptionistControlRoom from "./ReceptionistControlRoom";
import ReceptionClosed from "./ReceptionClosed";
import ReceptionOpen from "./ReceptionOpen";

const ReceptionMeetingRoom = ({
  sidebarCollapsed,
  companyProfile,
  hosts,
  meetingId,
  chatLoaded,
}) => {
  const history = useHistory();
  const { userProfile, companyId, role, fullName } = useContext(UserContext);
  const [meetingLink, setMeetingLink] = useState(null);
  const [meetingDetails, setMeetingDetails] = useState(null);
  const [receptionHosts, setReceptionHosts] = useState([]);
  const [userIsInviteeToMeeting, setUserIsInviteeToMeeting] = useState(null);
  const [hasLeftMeeting, setHasLeftMeeting] = useState(false);
  const { isReceiptionOpened } = useContext(MeetingContext);
  // Hook to notify when guest has left meeting
  useNotifyOnMeetingLeave({
    isLeavingReception: true,
    companyId,
    userProfile,
    role,
  });

  useEffect(() => {
    const loadCurrentReception = async (companyProfileId) => {
      const addGuestToReceiption = async () => {
        try {
          await guestArrivesAtReception(
            companyId,
            userProfile.AttendeeID,
            meetingId,
            APP_EVENT_ID
          );
        } catch (e) {
          console.error("Error adding guest to reception: ", e);
        }
      };

      if (companyId && userProfile) {
        addGuestToReceiption();
      }

      const _currentReception = await fetchCurrentReception(
        companyProfileId,
        APP_EVENT_ID,
        role === USER_ROLES.RECEPTIONIST
      );
      setReceptionHosts(_currentReception?.hosts);
      if (
        _currentReception &&
        _currentReception.reception &&
        _currentReception.reception.ReceptionOpen
      ) {
        const {
          ReceptionLink,
          ReceptionLinkGeneral,
        } = _currentReception.reception;
        setMeetingLink(
          role === USER_ROLES.RECEPTIONIST
            ? `${ReceptionLink}&${MEETING_QUERY_PARAMS}&displayName=${fullName}`
            : `${ReceptionLinkGeneral}?${MEETING_QUERY_PARAMS_GUESTS}&displayName=${fullName}`
        );
      }
    };

    const loadMeetingDetails = async (meetingId) => {
      try {
        const _meetingDetails = await fetchMeetingDetails(meetingId);
        setMeetingDetails(_meetingDetails);
      } catch (e) {
        console.error("Error loading meeting details: ", e);
      }
    };

    // If user has clicked on the "Leave Meeting" button, we simply do nothing
    // and let the UI redirect them to their destination.
    if (hasLeftMeeting) {
      return;
    }

    if (
      (userProfile && companyProfile && role) ||
      (isReceiptionOpened && userProfile && companyProfile && role)
    ) {
      loadCurrentReception(companyProfile.CompanyProfileID);
      if (role === USER_ROLES.DELEGATE && meetingId) {
        loadMeetingDetails(meetingId);
      }
    }
  }, [
    userProfile,
    companyId,
    role,
    fullName,
    companyProfile,
    meetingId,
    isReceiptionOpened,
    hasLeftMeeting,
  ]);

  useEffect(() => {
    if (userProfile && meetingDetails) {
      // Check if user is an invitee to the meeting; if not,
      // we can redirect them back to reception; otherwise,
      // notify the backend that the user has arrived to the
      // meeting.
      const userIsInvited = meetingDetails.PersonalEventsParticipants.some(
        (invitee) => invitee.AttendeeID === userProfile.AttendeeID
      );

      if (userIsInvited) {
        setUserIsInviteeToMeeting(true);
      } else {
        setUserIsInviteeToMeeting(false);
      }
    }
  }, [userProfile, meetingDetails]);

  useEffect(() => {
    if (
      chatLoaded & (role !== undefined) &&
      isReceiptionOpened !== undefined &&
      isReceiptionOpened &&
      companyProfile &&
      companyProfile.CompanyProfileID
    ) {
      window.MessagingHub.meetingSuite().set({
        CompanyID: companyProfile.CompanyProfileID,
        MeetingArea: "reception",
        MeetingID: meetingId,
        UserRole: role,
      });
    }
  }, [companyProfile, meetingId, chatLoaded, isReceiptionOpened, role]);

  const handleJoinMeeting = () => {
    history.push({
      pathname: `/${companyProfile.CompanyProfileID}/meeting/${meetingId}`,
      state: {
        redirectToMeeting: true,
        meetingDetails: meetingDetails,
      },
    });
  };

  const handleNotify = () => {
    console.log("notify host");
  };

  // Guests/Delegates should see the Receptionist as the only Attendee besides themselves in Reception
  let getFilteredAttendeesForDelegates = (receptionAttendeesAttendees) => {
    return receptionAttendeesAttendees.filter(
      (attendees) =>
        attendees.Role === USER_ROLES.RECEPTIONIST ||
        attendees.ProfileID === userProfile.ProfileID
    );
  };

  return (
    <>
      <MeetingContext.Consumer>
        {({ ismeetingOpened, receptionAttendeesList, isReceiptionOpened }) => (
          <>
            {isReceiptionOpened !== undefined && (
              <>
                {role === USER_ROLES.RECEPTIONIST ? (
                  <ReceptionistControlRoom
                    sidebarCollapsed={sidebarCollapsed}
                    meetingLink={meetingLink}
                    companyProfile={companyProfile}
                    companyId={companyId}
                    userProfile={userProfile}
                    role={role}
                    hosts={hosts}
                    receptionAttendeesList={receptionAttendeesList}
                    onLeaveMeeting={() => setHasLeftMeeting(true)}
                  />
                ) : isReceiptionOpened ? (
                  <ReceptionOpen
                    sidebarCollapsed={sidebarCollapsed}
                    meetingLink={meetingLink}
                    meetingId={meetingId}
                    meetingDetails={meetingDetails}
                    ismeetingOpened={ismeetingOpened}
                    companyProfile={companyProfile}
                    companyId={companyId}
                    userIsInviteeToMeeting={userIsInviteeToMeeting}
                    role={role}
                    attendees={
                      role === USER_ROLES.DELEGATE
                        ? getFilteredAttendeesForDelegates(
                            receptionAttendeesList
                          )
                        : receptionAttendeesList
                    }
                    onJoinMeeting={handleJoinMeeting}
                    onLeaveMeeting={() => setHasLeftMeeting(true)}
                  />
                ) : (
                  <ReceptionClosed
                    sidebarCollapsed={sidebarCollapsed}
                    meetingId={meetingId}
                    meetingDetails={meetingDetails}
                    ismeetingOpened={ismeetingOpened}
                    companyProfile={companyProfile}
                    userIsInviteeToMeeting={userIsInviteeToMeeting}
                    role={role}
                    hosts={receptionHosts}
                    onJoinMeeting={handleJoinMeeting}
                    onNotify={handleNotify}
                  />
                )}
              </>
            )}
          </>
        )}
      </MeetingContext.Consumer>
    </>
  );
};

export default ReceptionMeetingRoom;
